/**
 * IMPORT BUTTON — bottone "Importa" condiviso per tutte le liste entita'.
 *
 * Apre un file chooser filtrato sulle estensioni accettate (default JSON),
 * parsa il file come array JSON, e chiama `onEntities(items)` passando
 * l'array grezzo al consumer. Ogni consumer si occupa di mappare gli
 * elementi al proprio CreatePayload e di dispatch-are i thunk di create.
 *
 * Feedback centralizzato qui:
 *  - parse error / non-array → toast error
 *  - success → toast success con count
 *  - errore nel consumer (throw/reject) → toast error col message
 */

import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useToast } from './useToast';

interface ImportButtonProps {
  /**
   * Etichetta plurale dell'entita' (es. "Contatti", "Aziende"), usata nel
   * toast finale e nel log. Default generico "elementi".
   */
  entityLabel?: string;
  /**
   * Callback chiamata dopo parse OK del JSON. Riceve l'array grezzo:
   * il consumer fa il cast al proprio CreatePayload e dispatcha i create.
   * Puo' essere async; ImportButton mostra spinner finche' non risolve.
   */
  onEntities: (items: unknown[]) => Promise<void> | void;
  /** Estensioni accettate dal file chooser. Default JSON. */
  accept?: string;
  /**
   * Classe custom del trigger: se valorizzata, il bottone è un `<button>`
   * nativo con questa classe (es. "btn-ghost" del nuovo design) invece del
   * Button MUI. Senza className resta il Button MUI (comportamento invariato
   * per le liste non ancora migrate).
   */
  className?: string;
  /** Icona opzionale (leading) mostrata col trigger custom. */
  icon?: ReactNode;
}

export function ImportButton({
  entityLabel,
  onEntities,
  accept = '.json,application/json',
  className,
  icon,
}: ImportButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    setBusy(true);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        toast({ severity: 'error', message: t('import.invalidJson') });
        return;
      }
      if (!Array.isArray(parsed)) {
        toast({ severity: 'error', message: t('import.expectedArray') });
        return;
      }
      await onEntities(parsed);
      toast({
        severity: 'success',
        message: t('import.success', {
          count: parsed.length,
          entity: entityLabel ?? t('import.defaultEntity'),
        }),
      });
    } catch (err) {
      toast({
        severity: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {className ? (
        <button
          type="button"
          className={className}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? <CircularProgress size={14} /> : icon}
          {t('common.import')}
        </button>
      ) : (
        <Button
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          {t('common.import')}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          // Reset per poter riselezionare lo stesso file.
          e.target.value = '';
        }}
      />
    </>
  );
}
